import { Clear as ClearIcon, FileDownloadOutlined as FileDownloadOutlinedIcon, SearchOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import AdminInvitesTable from 'components/AdminInvitesTable';
import AdminMembersTable from 'components/AdminMembersTable';
import ButtonIcon from 'components/ButtonIcon';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { downloadOrganizationInvitesCSV, downloadOrganizationMembersCSV, getOrganizationInvites, getOrganizationMembers, getOrganizationMembersStats, getOrganizationsNames, User } from 'services/admin-users';
import { adminCancelInvitation, removeStaffbyInvitationID, updatePermission } from 'services/business-setting-service';
import { getErrorHtmlContent } from 'utils/utils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortInvitesOrder, setSortInvitesOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortInvitesBy, setSortInvitesBy] = useState('updatedAt');
  const [selectedOrganization, setSelectedOrganization] = useState<{ id: string; name: string } | null>(null);
  const [searchOrganization, setSearchOrganization] = useState('');
  const [searchType, setSearchType] = useState<'all' | 'email' | 'organization'>('all');
  const [searchEmail, setSearchEmail] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loadingDownloadCSV, setLoadingDownloadCSV] = useState(false);

  const { data: organizations, isLoading: isLoadingOrganizations } = useQuery('organizations', getOrganizationsNames, {
    onSuccess: (data) => {
      // Reset selection if the selected organization is no longer in the list
      if (selectedOrganization && data && !data.find(org => org.id === selectedOrganization.id)) {
        setSelectedOrganization(null);
        setSearchOrganization('');
        setSearchType('all');
      }
    },
    onError: (error) => {
      console.error('Error fetching organizations:', error);
    }
  });

  const { data: membersData, isLoading: isLoadingMembers, refetch: refetchMembers } = useQuery(
    ['members', page, rowsPerPage, sortBy, sortOrder, searchOrganization, searchType, searchEmail],
    () => getOrganizationMembers({
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy,
      sortOrder,
      searchOrganization,
      searchType,
      searchEmail
    })
  );
  const { data: invitesData, isLoading: isLoadingInvites, refetch: refetchInvites } = useQuery(
    ['invites', page, rowsPerPage, sortInvitesBy, sortInvitesOrder, searchOrganization, searchType, searchEmail],
    () => getOrganizationInvites({
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy: sortInvitesBy,
      sortOrder: sortInvitesOrder,
      searchOrganization,
      searchType,
      searchEmail
    })
  );
  const { data: membersStats } = useQuery(
    ['membersStats', selectedOrganization?.id],
    () => getOrganizationMembersStats(selectedOrganization?.id),
    { enabled: !!selectedOrganization?.id }
  );
  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string, type: 'members' | 'invites') => {
    if (type === 'members') {
      const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
      setSortOrder(isAsc ? 'desc' : 'asc');
      setSortBy(newOrderBy);
      setPage(0);
    } else {
      const isAsc = sortInvitesBy === newOrderBy && sortInvitesOrder === 'asc';
      setSortInvitesOrder(isAsc ? 'desc' : 'asc');
      setSortInvitesBy(newOrderBy);
      setPage(0);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };


  const handleActionClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleRemoveUser = async (id: string) => {
    if (!id) return;
    try {
      await removeStaffbyInvitationID(id);
      refetchMembers();
      enqueueSnackbar(t('BUSINESS_STAFF_REMOVE_SUCCESS'), {
        variant: 'success',
      });
    } catch (error: any) {
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.error('Error removing user:', error);
      }
    }
    handleActionClose();
  };

  const handleRemoveInvite = async (id: string, organizationId: string) => {
    if (!id) return;
    try {
      await adminCancelInvitation(id, organizationId);
      refetchInvites();
      enqueueSnackbar(t('BUSINESS_STAFF_INVITE_CANCEL_SUCCESS'), {
        variant: 'success',
      });
    } catch (error: any) {
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.error('Error removing invite:', error);
      }
    }
    handleActionClose();
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;
    try {
      await updatePermission(selectedUser.id, 'banned', selectedUser.organizationId);
      if (tabValue === 0) {
        refetchMembers();
      } else {
        refetchInvites();
      }
      enqueueSnackbar(t('ADMIN_USER_BAN_SUCCESS'), {
        variant: 'success',
      });
    } catch (error: any) {
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.error('Error banning user:', error);
      }
    }
    handleActionClose();
  };

  const handleViewOrganization = () => {
    if (selectedUser) {
      navigate(`/admin/business/${selectedUser.organizationId}/projects`);
    }
  };

  const handleDownloadCSV = async () => {
    setLoadingDownloadCSV(true);
    try {
      const params = {
        sortOrder: tabValue === 0 ? sortOrder : sortInvitesOrder,
        sortBy: tabValue === 0 ? sortBy : sortInvitesBy,
        searchOrganization,
        searchType,
        searchEmail
      };

      let blob: Blob;
      let filename: string;

      if (tabValue === 0) {
        // Download members CSV
        blob = await downloadOrganizationMembersCSV(params);
        filename = 'organization_members.csv';
      } else {
        // Download invites CSV
        blob = await downloadOrganizationInvitesCSV(params);
        filename = 'organization_invites.csv';
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(t('CSV_DOWNLOAD_SUCCESS'), {
        variant: 'success',
      });
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    } finally {
      setLoadingDownloadCSV(false);
    }
  };




  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            minHeight: "144px",
            mb: "22px",
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Box display={'flex'} alignItems={"center"} gap={"2rem"} flexWrap={"nowrap"} width={{ xs: "100%", md: "80%" }} >
              <Box>
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '24px',
                    lineHeight: '31px',
                    color: '#2F3B49',
                  }}
                >
                  {t('MENU_ADMIN_USERS')}
                </Typography>
              </Box>
              <Box width={"100%"}>
                <Autocomplete
                  options={Array.isArray(organizations) ? organizations : []}
                  getOptionLabel={(option) => option?.name || ''}
                  value={selectedOrganization}
                  onChange={(event, newValue) => {
                    setSelectedOrganization(newValue);
                    if (newValue) {
                      setSearchOrganization(newValue.id);
                      setSearchType('organization');
                    } else {
                      setSearchOrganization('');
                      setSearchType('all');
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  loading={isLoadingOrganizations}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('ADMIN_USER_SEARCH_BY_ORGANIZATION')}
                      fullWidth
                      sx={{
                        py: 0.5,
                        '& .MuiInputBase-root': {
                          height: '50px'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box width={"100%"}>
                <TextField
                  autoComplete='off'
                  id='standard-bare'
                  size='small'
                  fullWidth
                  variant='outlined'
                  placeholder={t('ADMIN_USER_SEARCH_BY_EMAIL_OR_NAME')}
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearchType('email');
                    }
                  }}
                  sx={{
                    width: { md: "100%", lg: "590px" },
                    '& .MuiInputBase-root': {
                      WebkitFontSmoothing: 'auto',
                      padding: "0px",
                    },
                    '& .MuiInputBase-inputAdornedStart': {
                      padding: 0,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {searchEmail ? (
                          <IconButton onClick={() => setSearchEmail('')}>
                            <ClearIcon />
                          </IconButton>
                        ) : (
                          <IconButton disabled>
                            <SearchOutlined />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                    sx: { height: '50px' }
                  }}
                />
              </Box>
            </Box>

            <Box display={'flex'} alignItems={"center"} gap={2}>
              <ButtonIcon
                onClick={handleDownloadCSV}
                disabled={loadingDownloadCSV}
              >
                {loadingDownloadCSV ? (
                  <CircularProgress size={24} />
                ) : (
                  <FileDownloadOutlinedIcon fontSize='large' />
                )}
              </ButtonIcon>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={t('ADMIN_USER_MEMBERS')} />
          <Tab label={t('ADMIN_USER_INVITES')} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0} sx={{ p: 0 }}>
        <AdminMembersTable
          data={membersData}
          page={page}
          rowsPerPage={rowsPerPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onRequestSort={(event, newOrderBy) => handleRequestSort(event, newOrderBy, 'members')}
          onRefetch={refetchMembers}
          onHandleRemoveMember={handleRemoveUser}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1} sx={{ p: 0 }}>
        <AdminInvitesTable
          data={invitesData}
          page={page}
          rowsPerPage={rowsPerPage}
          sortBy={sortInvitesBy}
          sortOrder={sortInvitesOrder}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onRequestSort={(event, newOrderBy) => handleRequestSort(event, newOrderBy, 'invites')}
          onRefetch={refetchInvites}
          onHandleRemoveInvite={handleRemoveInvite}
        />
      </TabPanel>
    </Box>
  );
};

export default AdminUserPage;
